export type KnowledgeCategory =
  | 'faq'
  | 'general_menu'
  | 'brunch_menu'
  | 'dessert_menu'
  | 'cake_menu'
  | 'allergy_menu'
  | 'gluten_free_menu'
  | 'vegan_menu'
  | 'private_events'
  | 'catering'
  | 'policy'
  | 'hours'
  | 'other';

export type KnowledgeScope =
  | 'general_dining'
  | 'private_events'
  | 'catering'
  | 'delivery_takeout'
  | 'allergy_dietary'
  | 'internal_reference'
  | 'unknown';

export type KnowledgeStatus =
  | 'uploaded'
  | 'processing'
  | 'processed'
  | 'failed'
  | 'archived';

export type ExtractionStatus =
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'failed'
  | 'skipped';

export type KnowledgeSourceKind = 'upload' | 'website' | 'generated_feedback';

export type KnowledgeSourceFile = {
  id: number;
  store: number;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  checksum: string;
  category: KnowledgeCategory;
  scope: KnowledgeScope;
  status: KnowledgeStatus;
  source_kind?: KnowledgeSourceKind;
  canonical_url?: string | null;
  token_count?: number | null;
  include_in_compilation: boolean;
  uploaded_by: number | null;
  extraction_status: ExtractionStatus;
  extraction_error: string;
  extracted_at: string | null;
  extraction_attempts: number;
  has_extracted_text: boolean;
  created_at: string;
  updated_at: string;
};

export const categoryOptions: Array<{
  value: KnowledgeCategory;
  label: string;
}> = [
  { value: 'faq', label: 'FAQ' },
  { value: 'general_menu', label: 'General menu' },
  { value: 'brunch_menu', label: 'Brunch menu' },
  { value: 'dessert_menu', label: 'Dessert menu' },
  { value: 'cake_menu', label: 'Cake menu' },
  { value: 'allergy_menu', label: 'Allergy menu' },
  { value: 'gluten_free_menu', label: 'Gluten-free menu' },
  { value: 'vegan_menu', label: 'Vegan menu' },
  { value: 'private_events', label: 'Private events' },
  { value: 'catering', label: 'Catering' },
  { value: 'policy', label: 'Policy' },
  { value: 'hours', label: 'Hours' },
  { value: 'other', label: 'Other' },
];

export const scopeOptions: Array<{ value: KnowledgeScope; label: string }> = [
  { value: 'general_dining', label: 'General dining' },
  { value: 'private_events', label: 'Private events' },
  { value: 'catering', label: 'Catering' },
  { value: 'delivery_takeout', label: 'Delivery / takeout' },
  { value: 'allergy_dietary', label: 'Allergy / dietary' },
  { value: 'internal_reference', label: 'Internal reference' },
  { value: 'unknown', label: 'Unknown' },
];

export const labelFor = <T extends string>(
  options: Array<{ value: T; label: string }>,
  value: T,
) => options.find((option) => option.value === value)?.label || value;

export type InferredKnowledgeMetadata = {
  category: KnowledgeCategory;
  scope: KnowledgeScope;
};

export const inferKnowledgeMetadata = (
  url: string,
  title = '',
): InferredKnowledgeMetadata => {
  const value = `${url} ${title}`.toLowerCase().replace(/[_-]+/g, ' ');
  if (/\b(private events?|banquets?|weddings?)\b/.test(value)) {
    return { category: 'private_events', scope: 'private_events' };
  }
  if (/\bcater(ing|ed)?\b/.test(value)) {
    return { category: 'catering', scope: 'catering' };
  }
  if (/\b(gluten|celiac)\b/.test(value)) {
    return { category: 'gluten_free_menu', scope: 'allergy_dietary' };
  }
  if (/\b(vegan|vegetarian)\b/.test(value)) {
    return { category: 'vegan_menu', scope: 'allergy_dietary' };
  }
  if (/\b(allerg|dietary)\b/.test(value)) {
    return { category: 'allergy_menu', scope: 'allergy_dietary' };
  }
  if (/\bbrunch\b/.test(value)) {
    return { category: 'brunch_menu', scope: 'general_dining' };
  }
  if (/\b(dessert|sweets?)\b/.test(value)) {
    return { category: 'dessert_menu', scope: 'general_dining' };
  }
  if (/\b(menu|food|dinner|lunch)\b/.test(value)) {
    return { category: 'general_menu', scope: 'general_dining' };
  }
  if (/\b(faq|questions?)\b/.test(value)) {
    return { category: 'faq', scope: 'general_dining' };
  }
  if (/\b(hours?|location|contact)\b/.test(value)) {
    return { category: 'hours', scope: 'general_dining' };
  }
  if (/\b(policy|policies|privacy|terms)\b/.test(value)) {
    return { category: 'policy', scope: 'general_dining' };
  }
  return { category: 'other', scope: 'unknown' };
};

export type CrawlerUrlValidation =
  | { ok: true; url: string }
  | { ok: false; error: string };

export const validateCrawlerUrl = (input: string): CrawlerUrlValidation => {
  const value = input.trim();
  if (!value) return { ok: false, error: 'Enter a website URL.' };
  if (/\s/.test(value)) {
    return { ok: false, error: 'Website URLs cannot contain spaces.' };
  }

  const hasScheme = /^[a-z][a-z\d+.-]*:/i.test(value);
  const candidate = hasScheme ? value : `https://${value}`;
  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return { ok: false, error: 'Enter a valid website URL.' };
  }

  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    return { ok: false, error: 'Only HTTP and HTTPS URLs are supported.' };
  }
  if (parsed.username || parsed.password) {
    return { ok: false, error: 'Website URLs cannot include credentials.' };
  }
  if (!parsed.hostname) {
    return { ok: false, error: 'The URL must include a hostname.' };
  }
  return { ok: true, url: parsed.toString() };
};
